import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, LogOut, ArrowLeft, KeyRound, Send, BarChart3, Lightbulb, CheckCircle2, Clock, XCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { CATEGORIES } from "@/data/resources";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_UI = {
  venter: { label: "Venter på godkjenning", icon: Clock, cls: "bg-muted text-muted-foreground" },
  godkjent: { label: "Godkjent og publisert", icon: CheckCircle2, cls: "bg-accent text-accent-foreground" },
  avvist: { label: "Avvist", icon: XCircle, cls: "bg-terracotta/10 text-terracotta" },
};

export default function Partner() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [category, setCategory] = useState("offentlig");
  const [description, setDescription] = useState("");

  const loadData = async () => {
    try {
      const [s, g] = await Promise.all([
        axios.get(`${API}/partner/stats`, { withCredentials: true }),
        axios.get(`${API}/partner/suggestions`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setSuggestions(g.data.suggestions || []);
    } catch (e) {
      if (e.response?.status === 401) setUser(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => {
        if (res.data?.role === "partner" || res.data?.role === "admin") setUser(res.data);
        else setUser(false);
      })
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/partner-login`, { code }, { withCredentials: true });
      setUser(data);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Noe gikk galt.");
    } finally {
      setLoading(false);
    }
  };

  const submitSuggestion = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/partner/suggestions`, { title, url, category, description }, { withCredentials: true });
      toast.success("Takk! Forslaget er sendt til godkjenning.");
      setTitle(""); setUrl(""); setDescription(""); setCategory("offentlig");
      loadData();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Kunne ikke sende forslaget.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  const maxDay = stats?.days?.length ? Math.max(...stats.days.map((d) => d.count), 1) : 1;

  return (
    <div className="min-h-screen bg-background text-foreground antialiased">
      <header className="border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="partner-back-link" className="flex items-center gap-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              <Compass size={16} />
            </span>
            <span className="font-serif font-bold text-lg text-foreground">Gjeldsveiviseren</span>
            <span className="hidden sm:inline-flex items-center gap-1 text-xs"><ArrowLeft size={12} /> Til forsiden</span>
          </Link>
          {user && (
            <button data-testid="partner-logout-btn" onClick={logout}
              className="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">
              <LogOut size={14} /> Logg ut
            </button>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        {checking ? (
          <p className="text-sm text-muted-foreground">Laster …</p>
        ) : !user ? (
          <div className="max-w-md mx-auto bg-surface border border-border rounded-3xl p-8 lg:p-10 mt-8">
            <h1 className="font-serif font-bold text-2xl flex items-center gap-2.5"><KeyRound size={20} className="text-primary" /> NAV-partner</h1>
            <p className="mt-2 text-sm text-muted-foreground">Logg inn med tilgangskoden dere har fått utdelt.</p>
            <form onSubmit={login} className="mt-6 space-y-4">
              <label className="block">
                <span className="text-xs font-semibold text-muted-foreground">Tilgangskode</span>
                <input data-testid="partner-code-input" type="password" required value={code} onChange={(e) => setCode(e.target.value)}
                  className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all duration-200"
                  placeholder="••••••••" />
              </label>
              {error && <p data-testid="partner-login-error" className="text-sm text-destructive">{error}</p>}
              <button data-testid="partner-login-submit" type="submit" disabled={loading}
                className="w-full bg-primary text-primary-foreground px-6 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 disabled:opacity-60">
                {loading ? "Logger inn …" : "Logg inn"}
              </button>
            </form>
          </div>
        ) : (
          <div data-testid="partner-dashboard" className="space-y-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-terracotta">NAV-partner</p>
              <h1 className="mt-2 font-serif font-bold tracking-tight text-2xl sm:text-3xl text-primary">Velkommen, {user.name}</h1>
              <p className="mt-2 text-sm text-muted-foreground">Her ser dere anonymisert statistikk og kan foreslå nye ressurser til katalogen.</p>
            </div>

            <section data-testid="partner-stats-section">
              <h2 className="flex items-center gap-2.5 font-serif font-bold text-xl"><BarChart3 size={19} className="text-primary" /> Anonymisert statistikk</h2>
              <p className="mt-1.5 text-xs text-muted-foreground">Ingen personopplysninger lagres — kun antall.</p>
              {stats && (
                <>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { label: "Sidevisninger", value: stats.pageviews },
                      { label: "AI-spørsmål stilt", value: stats.ai_questions },
                      { label: "Klikk til offentlige sider", value: stats.clicks },
                    ].map((c) => (
                      <div key={c.label} className="bg-surface border border-border rounded-2xl p-6">
                        <p className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">{c.label}</p>
                        <p className="mt-2 font-serif font-bold text-4xl">{c.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-surface border border-border rounded-2xl p-6">
                      <p className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">Sidevisninger siste 14 dager</p>
                      <div className="mt-5 flex items-end gap-1.5 h-28">
                        {stats.days.slice(-14).map((d) => (
                          <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5" title={`${d.date}: ${d.count}`}>
                            <div className="w-full bg-primary/80 rounded-t-md transition-all" style={{ height: `${Math.max((d.count / maxDay) * 100, 3)}%` }} />
                          </div>
                        ))}
                        {stats.days.length === 0 && <p className="text-sm text-muted-foreground">Ingen data ennå.</p>}
                      </div>
                    </div>
                    <div className="bg-surface border border-border rounded-2xl p-6">
                      <p className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">Mest klikkede ressurser</p>
                      <ul className="mt-5 space-y-3">
                        {stats.top_resources.map((r) => (
                          <li key={r.id} className="flex items-center justify-between gap-3 text-sm">
                            <span className="truncate">{r.title}</span>
                            <span className="font-mono text-xs font-semibold text-muted-foreground shrink-0">{r.clicks} klikk</span>
                          </li>
                        ))}
                        {stats.top_resources.length === 0 && <p className="text-sm text-muted-foreground">Ingen klikk registrert ennå.</p>}
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </section>

            <section data-testid="partner-suggest-section">
              <h2 className="flex items-center gap-2.5 font-serif font-bold text-xl"><Lightbulb size={19} className="text-primary" /> Foreslå ny ressurs</h2>
              <p className="mt-1.5 text-xs text-muted-foreground">Forslag publiseres først etter at sidens eier har godkjent dem.</p>
              <form onSubmit={submitSuggestion} className="mt-6 bg-surface border border-border rounded-2xl p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-xs font-semibold text-muted-foreground">Tittel</span>
                    <input data-testid="partner-suggest-title" required value={title} onChange={(e) => setTitle(e.target.value)}
                      className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all"
                      placeholder="F.eks. NAV — Barnetrygd" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-muted-foreground">Kategori</span>
                    <select data-testid="partner-suggest-category" value={category} onChange={(e) => setCategory(e.target.value)}
                      className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all">
                      {CATEGORIES.filter((c) => c.id !== "alle").map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </label>
                </div>
                <label className="block">
                  <span className="text-xs font-semibold text-muted-foreground">Lenke (https://…)</span>
                  <input data-testid="partner-suggest-url" type="url" required value={url} onChange={(e) => setUrl(e.target.value)}
                    className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm font-mono outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all"
                    placeholder="https://www.nav.no/…" />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-muted-foreground">Kort beskrivelse</span>
                  <textarea data-testid="partner-suggest-description" required rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                    className="mt-1.5 w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all resize-y"
                    placeholder="Hva hjelper denne siden med?" />
                </label>
                <button data-testid="partner-suggest-submit" type="submit" disabled={loading}
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 disabled:opacity-60">
                  <Send size={14} /> Send til godkjenning
                </button>
              </form>

              <div className="mt-6 space-y-3">
                {suggestions.map((s) => {
                  const st = STATUS_UI[s.status] || STATUS_UI.venter;
                  return (
                    <div key={s.id} data-testid="partner-suggestion-row" className="bg-surface border border-border rounded-xl px-5 py-4 flex flex-wrap items-center gap-3">
                      <div className="flex-1 min-w-[200px]">
                        <p className="text-sm font-semibold">{s.title}</p>
                        <p className="text-xs font-mono text-muted-foreground truncate">{s.url}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-mono font-semibold px-3 py-1.5 rounded-full ${st.cls}`}>
                        <st.icon size={11} /> {st.label}
                      </span>
                    </div>
                  );
                })}
                {suggestions.length === 0 && <p className="text-sm text-muted-foreground">Ingen forslag sendt ennå.</p>}
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
